import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Icon from '../../../../components/AppIcon';

// Mon=1 … Sun=7 in the app's other week helpers; JS getDay() is Sun=0 … Sat=6.
// Keys already exist in public/locales/{en,ar,fr}.
const WEEKDAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Single source of truth for how a UI status is drawn. Filled = a worked day,
// outlined = a non-worked day the company granted, tinted = week off.
// This is the complete, closed set of statuses the page can resolve (see the
// precedence block in ../index.jsx) — every cell drawn is explained here, and
// nothing listed here is unreachable. Keep the two in step.
// Abbreviations go through i18n so translators pick a letter that works in their
// script; the English defaults are collision-free (half-day and holiday no
// longer both read "H", late and leave no longer both read "L").
const STATUS_ORDER = [
  'present',
  'late',
  'early-leave',
  'half-day',
  'absent',
  'leave',
  'sick-leave',
  'holiday',
  'week-off',
];

const getStatusCircleClassName = (status) => {
  switch (status) {
    // Worked days — filled
    case 'present':
      return 'bg-success text-success-foreground';
    case 'late':
      return 'bg-warning text-warning-foreground';
    case 'early-leave':
      return 'bg-primary text-primary-foreground';
    case 'half-day':
      return 'bg-secondary text-secondary-foreground';
    case 'absent':
      return 'bg-destructive text-destructive-foreground';

    // Granted / non-worked days — outlined
    case 'leave':
      return 'bg-transparent border-2 border-warning text-warning';
    case 'sick-leave':
      return 'bg-transparent border-2 border-destructive text-destructive';
    case 'holiday':
      return 'bg-transparent border-2 border-secondary text-secondary';

    // Week off — quiet, it is not an exception worth a saturated colour
    case 'week-off':
      return 'bg-muted text-muted-foreground border border-border';

    default:
      return 'bg-muted text-muted-foreground';
  }
};

const AttendanceCalendar = ({ attendanceData = [], month, onMonthChange, onDateSelect, selectedDate }) => {
  const { t } = useTranslation();
  // `isGCCCountry` is emitted by GET /api/Account/GetUserInfo (UserView.IsGCCCountry), derived
  // server-side from the Country.IsGCC column — tenant country first, user country as fallback.
  // Read defensively: a redux-persist store rehydrated from a pre-upgrade session has no such key
  // until INIT lands, and `undefined` must mean Monday, not "throw".
  const isGcc = useSelector((state) => state.user?.isGCCCountry === true);
  const [internalMonth, setInternalMonth] = useState(new Date());
  const currentMonth = month ?? internalMonth;

  // GCC/Gulf tenants start their week on Sunday, everyone else on Monday —
  // mirrors the mobile app's `_startOfWeek` (timesheet.dart / scheduleWeek.dart).
  const weekStartsOnSunday = !!isGcc;
  const weekStartIndex = weekStartsOnSunday ? 0 : 1;

  const orderedWeekdayKeys = WEEKDAY_KEYS
    .slice(weekStartIndex)
    .concat(WEEKDAY_KEYS.slice(0, weekStartIndex));

  const getStatusText = (status) => {
    switch (status) {
      case 'present':
        return t('attendance.statusAbbr.present');
      case 'absent':
        return t('attendance.statusAbbr.absent');
      case 'leave':
        return t('attendance.statusAbbr.leave');
      case 'sick-leave':
        return t('attendance.statusAbbr.sickLeave');
      case 'holiday':
        return t('attendance.statusAbbr.holiday');
      case 'half-day':
        return t('attendance.statusAbbr.halfDay');
      case 'late':
        return t('attendance.statusAbbr.late');
      case 'early-leave':
        return t('attendance.statusAbbr.earlyLeave');
      case 'week-off':
        return t('attendance.statusAbbr.weekOff');
      default:
        return '';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present':
        return t('Present');
      case 'absent':
        return t('Absent');
      case 'leave':
        return t('Leave');
      case 'sick-leave':
        return t('Sick leave');
      case 'holiday':
        return t('Holiday');
      case 'half-day':
        return t('Half day');
      case 'late':
        return t('Late');
      case 'early-leave':
        return t('Early Clock out');
      case 'week-off':
        return t('Week Off');
      default:
        return status || '';
    }
  };

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const monthIndex = date?.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const daysInMonth = lastDay?.getDate();
    // Offset the first cell against the tenant's start-of-week rather than
    // assuming Sunday (getDay() === 0).
    const leadingBlanks = (firstDay.getDay() - weekStartIndex + 7) % 7;

    const days = [];

    for (let i = 0; i < leadingBlanks; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(monthIndex + 1)?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;
      const attendance = attendanceData?.find(item => item?.date === dateStr);

      days.push({
        day,
        date: dateStr,
        attendance
      });
    }

    return days;
  };

  const formatMonthYear = (date) => {
    return date?.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  };

  const navigateMonth = (direction) => {
    const base = currentMonth ?? new Date();
    const newDate = new Date(base);
    newDate?.setMonth(base?.getMonth() + direction);
    if (onMonthChange) {
      onMonthChange(newDate);
    } else {
      setInternalMonth(newDate);
    }
  };

  const getTodayKey = () => new Date()?.toLocaleDateString('en-CA');

  const isToday = (dateStr) => {
    const today = getTodayKey();
    return dateStr === today;
  };

  const isFutureDate = (dateStr) => {
    const today = getTodayKey();
    return dateStr > today;
  };

  const handleDateClick = (date) => {
    if (onDateSelect) {
      onDateSelect(date);
    }
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="w-full">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          aria-label={t('Previous month')}
          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors"
        >
          <Icon name="ChevronLeft" size={16} />
        </button>

        <h3 className="text-lg font-semibold text-foreground">
          {formatMonthYear(currentMonth)}
        </h3>

        <button
          type="button"
          onClick={() => navigateMonth(1)}
          aria-label={t('Next month')}
          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors"
        >
          <Icon name="ChevronRight" size={16} />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Week Day Headers */}
        {orderedWeekdayKeys?.map((key) => (
          <div key={key} className="p-2 text-center text-xs font-medium text-muted-foreground">
            {t(key)}
          </div>
        ))}

        {/* Calendar Days */}
        {days?.map((dayData, index) => {
          if (!dayData) {
            return <div key={`empty-${index}`} className="p-2 h-12"></div>;
          }

          const { day, date, attendance } = dayData;
          const isCurrentDay = isToday(date);
          const isFuture = isFutureDate(date);
          const isSelected = selectedDate === date;
          const statusLabel = attendance ? getStatusLabel(attendance?.status) : '';
          const timeRange = attendance
            ? [attendance?.checkIn, attendance?.checkOut].filter(Boolean).join(' – ')
            : '';
          // Attendance evidence outranks the roster's label, so a worked week
          // off or holiday reads as Present. The roster's word is not lost —
          // it is carried in the cell's accessible name and tooltip.
          const nominalNote = attendance?.workedOnNonWorkingDay
            ? t('attendance.workedOn', { status: getStatusLabel(attendance?.nominalStatus) })
            : '';
          const cellDescription = [statusLabel, nominalNote].filter(Boolean).join(' · ');

          return (
            <div
              key={date}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              aria-label={cellDescription ? `${day} — ${cellDescription}` : String(day)}
              onClick={() => handleDateClick(date)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDateClick(date);
                }
              }}
              className={`relative p-2 h-12 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                isSelected ? 'border-primary border-2' : isCurrentDay ? 'ring-2 ring-primary ring-offset-1 border-border/30' : 'border-border/30'
              } ${isFuture ? 'opacity-50' : ''}`}
            >
              <div className="text-xs font-medium text-foreground tabular-nums">{day}</div>

              {attendance && (
                <div className="absolute bottom-1 end-1">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold ${getStatusCircleClassName(attendance?.status)}`}
                    title={[cellDescription, timeRange].filter(Boolean).join(' · ')}
                  >
                    {getStatusText(attendance?.status)}
                  </div>
                </div>
              )}

              {!attendance && !isFuture && (
                <div className="absolute bottom-1 end-1">
                  <div className="w-2 h-2 rounded-full bg-border"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend — generated from the same status map the cells use, so the two
          can never drift apart. */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="text-xs text-muted-foreground mb-2">{t('Legend')}:</div>
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
          {STATUS_ORDER.map((status) => (
            <div key={status} className="flex items-center gap-2">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold ${getStatusCircleClassName(status)}`}
                aria-hidden="true"
              >
                {getStatusText(status)}
              </div>
              <span>{getStatusLabel(status)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AttendanceCalendar;
